// Company settings (/settings) — owner-only, and DEV_ONLY while the module is
// being built (the nav entry carries both gates; see mocks/nav.ts).
//
// Only the General tab has a body so far. The other two render a stub rather
// than PlaceholderPage, which brings its own <h1> and would nest a second page
// title under this one. Tab state is local, matching every other tabbed page —
// there is no URL sync for tabs anywhere in this app.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdditionalSettingsTab from './AdditionalSettingsTab';
import GeneralSettingsTab from './GeneralSettingsTab';
import { Tabs, type TabItem } from '@/components/common';

type CompanySettingsTab = 'general' | 'additional' | 'notifications';

const CompanySettingsPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<CompanySettingsTab>('general');

  const tabs: TabItem[] = [
    { value: 'general', label: t('company_settings_tab_general'), icon: 'sliders-horizontal' },
    { value: 'additional', label: t('company_settings_tab_additional'), icon: 'palette' },
    { value: 'notifications', label: t('company_settings_tab_notifications'), icon: 'bell' },
  ];

  return (
    <div className='company-settings'>
      <h1 className='company-settings__title'>{t('nav_settings')}</h1>

      <Tabs
        value={tab}
        onChange={(value) => setTab(value as CompanySettingsTab)}
        items={tabs}
        aria-label={t('nav_settings')}
      />

      {/* Mounted only while active, so an inactive tab fires no queries. */}
      {tab === 'general' && <GeneralSettingsTab />}
      {tab === 'additional' && <AdditionalSettingsTab />}
      {tab === 'notifications' && (
        <div className='company-settings__column'>
          <div className='company-settings__stub'>
            <p className='company-settings__stub-title'>{t('company_settings_stub_title')}</p>
            <p className='company-settings__stub-text'>{t('company_settings_stub_text')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySettingsPage;
